import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader } from "lucide-react";
import { toast } from "sonner";
import { API } from "../auth/AuthContext";
import {
  adminCancellationRequiresReason,
  cancellationError,
  cancellationPolicyText,
  partnerCancellationHint,
} from "../lib/cancellationDisplay";
import { formatMoney } from "../lib/currencyDisplay";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export default function CancellationReviewDialog({ booking, onClose, onCancelled }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const [adminReason, setAdminReason] = useState("");

  const loadQuote = useCallback(async () => {
    if (!booking?.booking_id) return;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${API}/bookings/${booking.booking_id}/cancellation-quote`,
        { withCredentials: true },
      );
      setQuote(response.data);
    } catch (requestError) {
      setQuote(null);
      setError(cancellationError(requestError.response?.data?.detail, "quote"));
    } finally {
      setLoading(false);
    }
  }, [booking?.booking_id]);

  useEffect(() => {
    if (booking) {
      setAdminReason("");
      loadQuote();
    }
  }, [booking, loadQuote]);

  const requiresAdminReason = adminCancellationRequiresReason(quote);
  const trimmedAdminReason = adminReason.trim();

  const confirmCancellation = async () => {
    if (!quote || confirming) return;
    if (requiresAdminReason && !trimmedAdminReason) {
      setError(cancellationError("admin_cancellation_reason_required", "confirm"));
      return;
    }
    setConfirming(true);
    setError("");
    try {
      const payload = {
        quote_id: quote.quote_id,
        policy_version: quote.policy_version,
      };
      if (requiresAdminReason) payload.reason = trimmedAdminReason;
      await axios.post(
        `${API}/bookings/${booking.booking_id}/cancel`,
        payload,
        { withCredentials: true },
      );
      toast.success("Booking cancelled");
      onCancelled?.();
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      setError(cancellationError(detail, "confirm"));
      if (["cancellation_quote_expired", "cancellation_quote_changed"].includes(detail)) {
        await loadQuote();
      }
    } finally {
      setConfirming(false);
    }
  };

  const currency = quote?.currency || booking?.currency || "USD";
  const partialBreakdown = quote?.partial_refund_breakdown;
  const nonRefundableValue = Number(
    quote?.non_refundable_value ?? quote?.retention_amount ?? 0,
  );
  const policyText = cancellationPolicyText(quote);
  const partnerHint = partnerCancellationHint(booking);

  return (
    <Dialog open={!!booking} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent
        className="bg-[var(--zc-bg-2)] border border-[var(--zc-border)]"
        data-testid="cancellation-review-dialog"
      >
        <DialogHeader>
          <DialogTitle className="zc-headline text-2xl">Review cancellation</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-10 grid place-items-center text-[var(--zc-text-2)]" data-testid="cancellation-quote-loading">
            <Loader size={20} className="animate-spin" aria-label="Calculating refund" />
          </div>
        )}

        {!loading && error && (
          <div className="p-3 rounded-xl border border-[var(--zc-danger)] text-sm text-[var(--zc-danger)] flex gap-2" role="alert">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {!loading && quote && (
          <>
            {quote.tier !== "none" && (
              <div className="zc-card p-5 bg-[var(--zc-bg-3)]">
                <div className="eyebrow">You will receive</div>
                <div className="zc-price text-3xl mt-1" data-testid="cancellation-cash-refund">
                  {formatMoney(quote.cash_refund, currency)}
                </div>
                {quote.zct_refund > 0 && (
                  <div className="text-sm text-[var(--zc-text-2)] mt-1">
                    plus {quote.zct_refund} ZCT returned
                  </div>
                )}
              </div>
            )}

            {partnerHint && (
              <div className="p-3 rounded-xl border border-[var(--zc-border)] text-sm text-[var(--zc-text-2)]" data-testid="cancellation-partner-hint">
                {partnerHint}
              </div>
            )}

            {policyText && (
              <div className="p-3 rounded-xl bg-[var(--zc-bg-3)] text-sm text-[var(--zc-text-2)]" data-testid="cancellation-policy-summary">
                {policyText}
              </div>
            )}

            <dl className="text-sm space-y-2">
              {quote.tier === "partial" && partialBreakdown?.lines?.length > 0 && (
                <>
                  <div className="text-xs font-semibold uppercase tracking-wide text-[var(--zc-text-3)] pt-1">
                    Refund breakdown
                  </div>
                  {partialBreakdown.lines.map((line) => (
                    <div
                      key={line.key}
                      className="flex justify-between gap-4"
                      data-testid={`cancellation-partial-${line.key}`}
                    >
                      <dt className="text-[var(--zc-text-2)]">{line.label}</dt>
                      <dd className="text-right">
                        <span className="text-[var(--zc-text-3)]">{formatMoney(line.amount, currency)}</span>
                        {" → "}
                        <span className="font-semibold">{formatMoney(line.refund_amount, currency)}</span>
                        {line.refund_rate_label ? (
                          <span className="text-[var(--zc-text-3)]"> ({line.refund_rate_label})</span>
                        ) : null}
                      </dd>
                    </div>
                  ))}
                </>
              )}
              {quote.tier !== "none" && quote.tier !== "partial" && (
                <>
                  <div className="flex justify-between gap-4">
                    <dt className="text-[var(--zc-text-2)]">Eligible rental base</dt>
                    <dd>{formatMoney(quote.refundable_base, currency)}</dd>
                  </div>
                  {Number(quote?.refundable_extras?.one_way_fee || 0) > 0 && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-[var(--zc-text-2)]">Eligible one-way extra</dt>
                      <dd>{formatMoney(quote.refundable_extras.one_way_fee, currency)}</dd>
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-between gap-4 font-semibold" data-testid="cancellation-non-refundable">
                <dt>
                  {quote.tier === "none"
                    ? "Non-refundable amount"
                    : quote.tier === "maximum" && nonRefundableValue > 0
                      ? "Amount retained by platform"
                      : nonRefundableValue > 0
                        ? "Non-refundable amount"
                        : "Cancellation fee"}
                </dt>
                <dd>{nonRefundableValue > 0 ? formatMoney(nonRefundableValue, currency) : "None"}</dd>
              </div>
            </dl>

            <p className="text-xs text-[var(--zc-text-3)]">
              This quote uses the {quote.timezone} pickup timezone and is recalculated when you confirm.
              {" "}<Link to="/cancellation" className="text-[var(--zc-accent)] font-semibold hover:underline">Cancellation policy</Link>
            </p>

            {requiresAdminReason && (
              <div className="space-y-2" data-testid="cancellation-admin-reason">
                <label htmlFor="cancellation-admin-reason" className="text-sm font-semibold text-[var(--zc-text)]">
                  Admin cancellation reason
                </label>
                <textarea
                  id="cancellation-admin-reason"
                  className="zc-input w-full p-3 text-sm h-24"
                  value={adminReason}
                  maxLength={500}
                  onChange={(event) => setAdminReason(event.target.value)}
                  placeholder="Why is support cancelling this booking?"
                  data-testid="cancellation-admin-reason-input"
                />
                <p className="text-xs text-[var(--zc-text-3)]">
                  Required for audit. This booking receives a full refund.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button type="button" className="zc-btn-ghost h-10 px-4 text-sm" onClick={onClose}>
                Keep booking
              </button>
              <button
                type="button"
                className="zc-btn-primary h-10 px-4 text-sm disabled:opacity-50"
                disabled={confirming || (requiresAdminReason && !trimmedAdminReason)}
                onClick={confirmCancellation}
                data-testid="cancellation-confirm"
              >
                {confirming ? "Cancelling…" : "Confirm cancellation"}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
